import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation } from 'react-router-dom';
import enMessages from '@cloudscape-design/components/i18n/messages/all.en';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import Auth0ProviderWithHistory from './auth/auth0-provider';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import queryString from 'query-string';

import * as Sentry from '@sentry/react';
import { Route, Routes } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { NavigationContext } from './common/navigation';

import HomePage from './views/home';
import CreatePage from './views/create';
import CreateCodePage from './views/create/code';
import ErrorPage from './error';
import CardPage from './views/card';
import CardPdfPage from './views/card/pdf';
import '@cloudscape-design/global-styles/index.css';
import './global.css';
import { AuthRequired } from './auth/auth-wrapper';
import { liveApiUrl, useAxiosInterceptors } from './common/api/axiosInstance';
import { getErrorDetails } from './components/Loading';
import { useAuth0 } from '@auth0/auth0-react';
import { AxiosError } from 'axios';
import DripEmailReportPage from './views/drip/emails/email/report';
import DripContactOptOutPage from './views/drip/contacts/contact/opt-out';

Sentry.init({
  dsn: 'https://3d4f2949823500d9e9b25b3d142771a8@o4507625924722688.ingest.us.sentry.io/4508846988066816',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === 'development' ? 0.0 : 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', liveApiUrl],
  // Session Replay
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: process.env.NODE_ENV === 'development' ? 0.0 : 1.0,
  environment: process.env.NODE_ENV === 'development' ? 'development' : 'production',
  enabled: process.env.NODE_ENV === 'development' ? false : true,
});

function NavRoutes() {
  useAxiosInterceptors();
  const { pathname, hash, key, search } = useLocation();

  const { isAuthenticated, user } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    const sentryUserDetails = {
      id: user?.sub,
      email: user?.email,
      name: user?.name,
    };
    Sentry.setUser(sentryUserDetails);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (hash !== '') {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    } else if (!search) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, key]); // do this on route change

  return (
    <>
      <Sentry.ErrorBoundary
        fallback={
          // TODO: Add a custom error page
          <ErrorPage />
        }
      >
        <Routes>
          <Route
            element={
              <AuthRequired>
                <HomePage />
              </AuthRequired>
            }
            path="/"
          />
          <Route
            // public route to create a report card
            element={<CreatePage />}
            path="/create"
          />
          <Route
            // public route to wait for a completed report card
            element={<CreateCodePage />}
            path="/create/:public_code"
          />
          <Route element={<DripContactOptOutPage />} path="/drip/contacts/:uuid/opt-out" />
          <Route element={<DripEmailReportPage />} path="/drip/emails/:uuid/report" />
          <Route element={<CardPage />} path="/:uuid" />
          <Route element={<CardPdfPage />} path="/:uuid/:period" />
          <Route element={<ErrorPage />} path="*" />
        </Routes>
      </Sentry.ErrorBoundary>
    </>
  );
}

function AppWithUserContext() {
  const { innerWidth: width } = window;
  const navigationSize = 200;

  const [navigationOpen, setNavigationOpen] = useState(width > 688);

  const { isAuthenticated, logout } = useAuth0();

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: async (error, query) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 401 &&
            isAuthenticated
          ) {
            // Logout user, needs to re-login because of expired refresh token
            await logout({ logoutParams: { returnTo: window.location.origin } });
            return;
          }
          const { title, description } = getErrorDetails(error);
          // Report the error to Sentry
          Sentry.captureException(error, {
            tags: {
              queryKey: `${query.queryKey}`,
              title,
              description,
            },
          });
        },
      }),
      mutationCache: new MutationCache({
        onError: async (error) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 401 &&
            isAuthenticated
          ) {
            // Logout user, needs to re-login because of expired refresh token
            await logout({ logoutParams: { returnTo: window.location.origin } });
            return;
          }
          const { title, description } = getErrorDetails(error);
          // Report the error to Sentry
          Sentry.captureException(error, {
            tags: {
              title,
              description,
            },
          });
        },
      }),
    });
  }, [isAuthenticated, logout]);

  return (
    <QueryClientProvider client={queryClient}>
      <NavigationContext.Provider value={{ navigationOpen, setNavigationOpen, navigationSize }}>
        <NavRoutes />
      </NavigationContext.Provider>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Auth0ProviderWithHistory>
        <QueryParamProvider
          adapter={ReactRouter6Adapter}
          options={{
            searchStringToObject: queryString.parse,
            objectToSearchString: queryString.stringify,
            removeDefaultsFromUrl: true,
          }}
        >
          <I18nProvider locale="en" messages={[enMessages]}>
            <AppWithUserContext />
          </I18nProvider>
        </QueryParamProvider>
      </Auth0ProviderWithHistory>
    </BrowserRouter>
  );
}

export default App;
